<div class="pricing-container">

  <!-- ── Page Header ─────────────────────────────────────────────── -->
  <div class="page-header">
    <div class="page-header-text">
      <h1>Scrap Pricing</h1>
      <p>Manage and update scrap item prices</p>
      <div *ngIf="viewMode === 'global' && hasChanges()" class="unsaved-badge">
        <i class="fas fa-exclamation-triangle"></i>
        {{ getModifiedCount() }} unsaved change{{ getModifiedCount() !== 1 ? 's' : '' }}
      </div>
      <div *ngIf="viewMode === 'groups' && hasGroupChanges()" class="unsaved-badge">
        <i class="fas fa-exclamation-triangle"></i>
        {{ getGroupModifiedCount() }} unsaved override change{{ getGroupModifiedCount() !== 1 ? 's' : '' }}
      </div>
    </div>
    <div class="page-header-actions">
      <!-- Tabs for View Mode -->
      <div class="view-mode-tabs">
        <button class="tab-btn" [class.active]="viewMode === 'global'" (click)="onViewModeChange('global')">
          Global Base Prices
        </button>
        <button class="tab-btn" [class.active]="viewMode === 'groups'" (click)="onViewModeChange('groups')">
          Zone Pricing Groups
        </button>
      </div>

      <button class="btn btn-ghost" (click)="viewMode === 'global' ? resetPrices() : loadPricingGroups()" [disabled]="loading || saving">
        <i class="fas fa-sync-alt"></i> Refresh
      </button>

      <!-- Save Changes for Global Prices -->
      <button *ngIf="viewMode === 'global'" class="btn btn-save" (click)="updatePrices()"
        [disabled]="loading || saving || !isFormValid() || !hasChanges()">
        <i class="fas" [ngClass]="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
        {{ saving ? 'Saving...' : 'Save Changes' }}
        <span class="badge" *ngIf="hasChanges() && !saving">{{ getModifiedCount() }}</span>
      </button>

      <!-- Save Changes for Pricing Groups -->
      <button *ngIf="viewMode === 'groups'" class="btn btn-save" (click)="savePricingGroupChanges()"
        [disabled]="loading || saving || !selectedPricingGroup || !hasGroupChanges()">
        <i class="fas" [ngClass]="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
        {{ saving ? 'Saving...' : 'Save Changes' }}
        <span class="badge" *ngIf="hasGroupChanges() && !saving">{{ getGroupModifiedCount() }}</span>
      </button>

      <!-- Create and Export Buttons only in global mode -->
      <button *ngIf="viewMode === 'global'" class="btn btn-create" (click)="openCreateScrapItemModal()">
        <i class="fas" [ngClass]="showCreateScrapItemModal ? 'fa-times' : 'fa-plus'"></i>
        {{ showCreateScrapItemModal ? 'Cancel' : 'New Item' }}
      </button>
      <button *ngIf="viewMode === 'global'" class="btn btn-export" (click)="exportToPdf()"
        [disabled]="loading || exportingPdf || getFilteredItems().length === 0">
        <i class="fas" [ngClass]="exportingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
        {{ exportingPdf ? 'Exporting...' : 'Export PDF' }}
        <span class="badge" *ngIf="getSelectedCount() > 0 && !exportingPdf">{{ getSelectedCount() }}</span>
      </button>
    </div>
  </div>

  <!-- ── Notifications ───────────────────────────────────────────── -->
  <div *ngIf="errorMessage" class="alert alert-error">
    <div class="alert-body">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
    </div>
    <button class="alert-retry" (click)="loadScrapPrices()" [disabled]="loading">
      <i class="fas fa-redo"></i> Retry
    </button>
  </div>
  <div *ngIf="successMessage" class="alert alert-success">
    <i class="fas fa-check-circle"></i>
    <span>{{ successMessage }}</span>
  </div>

  <!-- ── Inline Create Panel ─────────────────────────────────────── -->
  <div class="create-panel" *ngIf="showCreateScrapItemModal && viewMode === 'global'">
    <div class="create-panel-header">
      <h2><i class="fas fa-plus-circle"></i> New Scrap Item</h2>
    </div>
    <div class="create-panel-body">
      <app-create-scrap-item (created)="onScrapItemCreated()"></app-create-scrap-item>
    </div>
  </div>

  <!-- ── Pricing Groups Selector Bar ────────────────────────────── -->
  <div class="groups-selector-bar" *ngIf="viewMode === 'groups' && !loading">
    <div class="group-select-wrapper">
      <label class="filter-label">Active Pricing Group</label>
      <select class="group-select" [ngModel]="selectedPricingGroup" (ngModelChange)="selectPricingGroup($event)" [compareWith]="compareGroups">
        <option *ngFor="let group of pricingGroups" [ngValue]="group">
          {{ group.name }} ({{ group.items.length }} override{{ group.items.length !== 1 ? 's' : '' }})
        </option>
      </select>
    </div>
  </div>

  <!-- ── Filters ─────────────────────────────────────────────────── -->
  <div class="filters-bar" *ngIf="!loading && scrapItems.length > 0 && (viewMode === 'global' || selectedPricingGroup)">
    <div class="filter-group">
      <label class="filter-label">Category</label>
      <select class="filter-select" [(ngModel)]="selectedCategory" (change)="onCategoryChange(selectedCategory)">
        <option value="all">All ({{ scrapItems.length }})</option>
        <option *ngFor="let cat of getUniqueCategories()" [value]="cat">
          {{ cat }} ({{ getCategoryCount(cat) }})
        </option>
      </select>
    </div>
    <div class="filter-group filter-search">
      <label class="filter-label">Search</label>
      <div class="search-wrap">
        <i class="fas fa-search search-icon"></i>
        <input type="text" class="filter-input" [(ngModel)]="searchTerm" (input)="applyFilter()"
          placeholder="Search by name...">
      </div>
    </div>
    <div class="filter-group filter-export-opt" *ngIf="viewMode === 'global'">
      <label class="filter-label">Export options</label>
      <label class="checkbox-label">
        <input type="checkbox" [(ngModel)]="includeDealerPrice">
        <span>Include dealer price</span>
      </label>
    </div>
    <div class="filter-count">
      <span>{{ getFilteredItems().length }} / {{ scrapItems.length }} items</span>
      <span *ngIf="viewMode === 'global' && getSelectedCount() > 0" class="selected-count">
        · {{ getSelectedCount() }} selected
      </span>
    </div>
  </div>

  <!-- ── Loading ─────────────────────────────────────────────────── -->
  <div *ngIf="loading || (viewMode === 'groups' && loadingGroups)" class="loading-state">
    <div class="spinner"></div>
    <span>Loading scrap prices...</span>
  </div>

  <!-- ── Desktop Global Table ────────────────────────────────────── -->
  <div *ngIf="viewMode === 'global' && !loading && getFilteredItems().length > 0" class="table-wrapper">
    <table class="pricing-table">
      <thead>
        <tr>
          <th class="col-check">
            <input type="checkbox"
              [checked]="getSelectedCount() === getFilteredItems().length && getFilteredItems().length > 0"
              (change)="selectAll()">
          </th>
          <th class="col-details">Item Details</th>
          <th class="col-image">Image</th>
          <th class="col-cat">Category</th>
          <th class="col-unit">Unit</th>
          <th class="col-sort">Sort Order</th>
          <th class="col-price">Regular (₹)</th>
          <th class="col-price">Special (₹)</th>
          <th class="col-dealer">Dealer %</th>
          <th class="col-price">Customer (₹)</th>
          <th class="col-toggle">Active</th>
          <th class="col-toggle">Featured</th>
          <th class="col-toggle">Scrap</th>
          <th class="col-toggle">Visible</th>
          <th class="col-action"></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let item of getFilteredItems(); trackBy: trackByScrapItemId"
          [class.row-modified]="isItemModified(item)">

          <!-- Checkbox -->
          <td class="col-check">
            <input type="checkbox" [checked]="isSelected(item.scrapItemId)"
              (change)="toggleSelection(item.scrapItemId)">
          </td>

          <!-- Item Details: avatar + name + alt names -->
          <td class="col-details">
            <div class="item-details-cell">
              <div class="item-avatar" *ngIf="!shouldShowFallback(item)">
                <img [src]="item.imageName" [alt]="item.scrapName"
                  (error)="onImageError($event, item)" (load)="onImageLoad($event, item)">
              </div>
              <div class="item-avatar fallback" *ngIf="shouldShowFallback(item)"
                [class]="getFallbackStyle(item)">
                {{ item.scrapName.charAt(0).toUpperCase() }}
              </div>
              <div class="item-name-wrap">
                <input type="text" [(ngModel)]="item.scrapName" class="tbl-input"
                  [class.modified]="isItemModified(item)" placeholder="Name">
                <div class="alt-names-mini">
                  <div class="alt-input-row">
                    <input #altInput type="text" class="tbl-input tbl-input-xs"
                      placeholder="Add alt name"
                      (keydown)="addAlternativeNameFromInput(item, $event)">
                    <button class="btn-mini-add" (click)="addAlternativeName(item, altInput)">+</button>
                  </div>
                  <div class="alt-tags-mini" *ngIf="item.alternativeNames?.length">
                    <span *ngFor="let alt of item.alternativeNames; let i = index" class="alt-tag-mini">
                      {{ alt }}
                      <button (click)="removeAlternativeName(item, i)"><i class="fas fa-times"></i></button>
                    </span>
                  </div>
                </div>
              </div>
              <i *ngIf="isItemModified(item)" class="fas fa-circle modified-dot" title="Modified"></i>
            </div>
          </td>

          <!-- Image name -->
          <td class="col-image">
            <input type="text" [(ngModel)]="item.imageName" class="tbl-input tbl-input-xs"
              [class.modified]="isItemModified(item)" placeholder="filename.png">
          </td>

          <!-- Category -->
          <td class="col-cat">
            <select [(ngModel)]="item.scrapCategory" (ngModelChange)="onItemCategoryChange(item)"
              class="tbl-select" [class.modified]="isItemModified(item)">
              <option *ngFor="let cat of categories" [value]="cat.name">{{ cat.name }}</option>
            </select>
          </td>

          <!-- Unit -->
          <td class="col-unit">
            <select [(ngModel)]="item.unit" class="tbl-select" [class.modified]="isItemModified(item)">
              <option value="Kg">Kg</option>
              <option value="Pc">Pc</option>
            </select>
          </td>

          <!-- Sort Order -->
          <td class="col-sort">
            <input type="number" [(ngModel)]="item.sortOrder" class="tbl-input tbl-num"
              [class.modified]="isItemModified(item)" placeholder="0">
          </td>

          <!-- Regular Price -->
          <td class="col-price">
            <input type="number" [(ngModel)]="item.regularPrice" step="0.01" class="tbl-input tbl-num"
              [class.modified]="isItemModified(item)" [class.input-invalid]="isValueNaN(item.regularPrice)">
          </td>

          <!-- Special Price -->
          <td class="col-price">
            <input type="number" [(ngModel)]="item.specialPrice" step="0.01" class="tbl-input tbl-num"
              [class.modified]="isItemModified(item)" [class.input-invalid]="isValueNaN(item.specialPrice)">
          </td>

          <!-- Dealer % -->
          <td class="col-dealer">
            <input type="number" [(ngModel)]="item.dealerPercentage" min="0" max="100" step="0.01"
              class="tbl-input tbl-num"
              [class.modified]="isItemModified(item)"
              [class.input-invalid]="item.dealerPercentage < 0 || item.dealerPercentage > 100">
          </td>

          <!-- Customer Price (editable; reverse-calculates Dealer %) -->
          <td class="col-price">
            <input type="number" class="tbl-input tbl-num computed-price-input"
              [class.modified]="isItemModified(item)"
              [value]="getGlobalCustomerPrice(item).toFixed(2)"
              (change)="onGlobalCustomerPriceChange(item, $any($event.target).value, $any($event.target))"
              min="0" [max]="item.regularPrice" step="0.01" title="Enter a customer price to back-calculate the Dealer %">
          </td>

          <!-- Active -->
          <td class="col-toggle"><label class="toggle-cell">
            <input type="checkbox" [(ngModel)]="item.isActive">
            <span [class]="item.isActive ? 'pill pill-green' : 'pill pill-red'">
              {{ item.isActive ? 'Yes' : 'No' }}
            </span>
          </label></td>

          <!-- Featured -->
          <td class="col-toggle"><label class="toggle-cell">
            <input type="checkbox" [(ngModel)]="item.isFeatured">
            <span [class]="item.isFeatured ? 'pill pill-blue' : 'pill pill-gray'">
              {{ item.isFeatured ? 'Yes' : 'No' }}
            </span>
          </label></td>

          <!-- Is Scrap -->
          <td class="col-toggle"><label class="toggle-cell">
            <input type="checkbox" [(ngModel)]="item.isScrap">
            <span [class]="item.isScrap ? 'pill pill-purple' : 'pill pill-gray'">
              {{ item.isScrap ? 'Yes' : 'No' }}
            </span>
          </label></td>

          <!-- Visible -->
          <td class="col-toggle"><label class="toggle-cell">
            <input type="checkbox" [(ngModel)]="item.isVisibleToCustomer">
            <span [class]="item.isVisibleToCustomer ? 'pill pill-teal' : 'pill pill-gray'">
              {{ item.isVisibleToCustomer ? 'Yes' : 'No' }}
            </span>
          </label></td>

          <!-- Delete -->
          <td class="col-action">
            <button class="btn-delete" (click)="requestDelete(item)" title="Delete">
              <i class="fas fa-trash"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- ── Desktop Overrides Table ─────────────────────────────────── -->
  <div *ngIf="viewMode === 'groups' && !loading && selectedPricingGroup && getFilteredItems().length > 0" class="table-wrapper">
    <table class="pricing-table">
      <thead>
        <tr>
          <th class="col-override-toggle" style="text-align: center;">Override</th>
          <th class="col-override-details">Item Details</th>
          <th class="col-override-cat">Category</th>
          <th class="col-override-unit">Unit</th>
          <th class="col-override-global-reg" style="text-align: right;">Global Reg (₹)</th>
          <th class="col-override-type">Override Type</th>
          <th class="col-override-val">Override Value</th>
          <th class="col-override-dealer">Dealer %</th>
          <th class="col-override-preview-reg" style="text-align: right;">Preview Reg (₹)</th>
          <th class="col-override-preview-cust" style="text-align: right;">Preview Cust (₹)</th>
          <th class="col-override-active" style="text-align: center;">Active</th>
          <th class="col-override-visible" style="text-align: center;">Visible</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let item of getFilteredItems(); trackBy: trackByScrapItemId"
          [class.row-modified]="isOverrideModified(item)">

          <!-- Override status toggle -->
          <td class="col-override-toggle" style="text-align: center;">
            <label class="toggle-cell" style="align-items: center; justify-content: center;">
              <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].overridden">
              <span [class]="overrideStates[item.scrapItemId].overridden ? 'pill pill-green' : 'pill pill-gray'">
                {{ overrideStates[item.scrapItemId].overridden ? 'Yes' : 'No' }}
              </span>
            </label>
          </td>

          <!-- Item details -->
          <td class="col-override-details">
            <div class="item-details-cell">
              <div class="item-avatar" *ngIf="!shouldShowFallback(item)">
                <img [src]="item.imageName" [alt]="item.scrapName"
                  (error)="onImageError($event, item)" (load)="onImageLoad($event, item)">
              </div>
              <div class="item-avatar fallback" *ngIf="shouldShowFallback(item)"
                [class]="getFallbackStyle(item)">
                {{ item.scrapName.charAt(0).toUpperCase() }}
              </div>
              <div class="item-name-wrap">
                <span style="font-weight: 600; color: #111827;">{{ item.scrapName }}</span>
                <div class="alt-tags-mini" *ngIf="item.alternativeNames?.length" style="margin-top: 0.15rem;">
                  <span *ngFor="let alt of item.alternativeNames" class="alt-tag-mini" style="font-size: 0.65rem; padding: 0.1rem 0.35rem;">
                    {{ alt }}
                  </span>
                </div>
              </div>
            </div>
          </td>

          <!-- Category -->
          <td class="col-override-cat">
            <span class="cat-badge" style="max-width: 110px;">{{ item.scrapCategory }}</span>
          </td>

          <!-- Unit -->
          <td class="col-override-unit">
            <span style="font-weight: 500; color: #475569;">{{ item.unit }}</span>
          </td>

          <!-- Global Base Regular Price -->
          <td class="col-override-global-reg" style="text-align: right; font-weight: 500; color: #475569;">
            {{ item.regularPrice.toFixed(2) }}
          </td>

          <!-- Override Type -->
          <td class="col-override-type">
            <select [(ngModel)]="overrideStates[item.scrapItemId].pricingType" class="tbl-select"
              [disabled]="!overrideStates[item.scrapItemId].overridden">
              <option [ngValue]="0">Percentage (%)</option>
              <option [ngValue]="1">Fixed (₹)</option>
            </select>
          </td>

          <!-- Override Value -->
          <td class="col-override-val">
            <input type="number" [(ngModel)]="overrideStates[item.scrapItemId].value" class="tbl-input tbl-num"
              [disabled]="!overrideStates[item.scrapItemId].overridden"
              min="0">
          </td>

          <!-- Dealer % Override -->
          <td class="col-override-dealer">
            <input type="number" [(ngModel)]="overrideStates[item.scrapItemId].dealerPercentage"
              [placeholder]="item.dealerPercentage" class="tbl-input tbl-num"
              [disabled]="!overrideStates[item.scrapItemId].overridden"
              min="0" max="100" step="0.1">
          </td>

          <!-- Preview Regular Price (editable; reverse-calculates Override Value) -->
          <td class="col-override-preview-reg" style="text-align: right;">
            <span *ngIf="overrideStates[item.scrapItemId].overridden" class="original-price-tag">{{ item.regularPrice.toFixed(2) }}</span>
            <input type="number" class="tbl-input tbl-num preview-input preview-input-reg"
              [class.override-active]="overrideStates[item.scrapItemId].overridden"
              [value]="getPreviewPrice(item).toFixed(2)"
              (change)="onPreviewRegularPriceChange(item, $any($event.target).value, $any($event.target))"
              [disabled]="!overrideStates[item.scrapItemId].overridden"
              min="0" step="0.5" title="Enter a regular price to back-calculate the Override Value">
          </td>

          <!-- Preview Customer Price (editable; reverse-calculates Dealer %) -->
          <td class="col-override-preview-cust" style="text-align: right;">
            <span *ngIf="overrideStates[item.scrapItemId].overridden" class="original-price-tag">
              {{ (item.regularPrice - (item.regularPrice * item.dealerPercentage / 100)).toFixed(2) }}
            </span>
            <input type="number" class="tbl-input tbl-num preview-input preview-input-cust"
              [class.override-active]="overrideStates[item.scrapItemId].overridden"
              [value]="getPreviewCustomerPrice(item).toFixed(2)"
              (change)="onPreviewCustomerPriceChange(item, $any($event.target).value, $any($event.target))"
              [disabled]="!overrideStates[item.scrapItemId].overridden"
              min="0" [max]="getPreviewPrice(item)" step="0.5" title="Enter a customer price to back-calculate the Dealer %">
          </td>

          <!-- Active Override -->
          <td class="col-override-active" style="text-align: center;">
            <label class="toggle-cell" style="align-items: center; justify-content: center;">
              <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].isActive"
                [disabled]="!overrideStates[item.scrapItemId].overridden">
              <span [class]="overrideStates[item.scrapItemId].isActive ? 'pill pill-green' : 'pill pill-red'">
                {{ overrideStates[item.scrapItemId].isActive ? 'Yes' : 'No' }}
              </span>
            </label>
          </td>

          <!-- Visible Override -->
          <td class="col-override-visible" style="text-align: center;">
            <label class="toggle-cell" style="align-items: center; justify-content: center;">
              <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].isVisibleToCustomer"
                [disabled]="!overrideStates[item.scrapItemId].overridden">
              <span [class]="overrideStates[item.scrapItemId].isVisibleToCustomer ? 'pill pill-teal' : 'pill pill-gray'">
                {{ overrideStates[item.scrapItemId].isVisibleToCustomer ? 'Yes' : 'No' }}
              </span>
            </label>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- ── Mobile Global Cards ────────────────────────────────────── -->
  <div *ngIf="viewMode === 'global' && !loading && getFilteredItems().length > 0" class="mobile-list">
    <div *ngFor="let item of getFilteredItems(); trackBy: trackByScrapItemId"
      class="mobile-card" [class.mobile-card-modified]="isItemModified(item)">

      <!-- Card Header -->
      <div class="card-header">
        <label class="card-check">
          <input type="checkbox" [checked]="isSelected(item.scrapItemId)"
            (change)="toggleSelection(item.scrapItemId)">
        </label>
        <div class="card-avatar" *ngIf="!shouldShowFallback(item)">
          <img [src]="item.imageName" [alt]="item.scrapName"
            (error)="onImageError($event, item)" (load)="onImageLoad($event, item)">
        </div>
        <div class="card-avatar fallback" *ngIf="shouldShowFallback(item)"
          [class]="getFallbackStyle(item)">
          {{ item.scrapName.charAt(0).toUpperCase() }}
        </div>
        <div class="card-title-area">
          <input type="text" [(ngModel)]="item.scrapName" class="card-name-input"
            [class.modified]="isItemModified(item)" placeholder="Item name">
          <div class="card-meta">
            <select [(ngModel)]="item.scrapCategory" (ngModelChange)="onItemCategoryChange(item)"
              class="cat-select" [class.modified]="isItemModified(item)">
              <option *ngFor="let cat of categories" [value]="cat.name">{{ cat.name }}</option>
            </select>
            <i *ngIf="isItemModified(item)" class="fas fa-edit modified-indicator"></i>
          </div>
        </div>
      </div>

      <!-- Image name -->
      <div class="card-field">
        <label class="card-label">Image filename</label>
        <input type="text" [(ngModel)]="item.imageName" class="card-input"
          [class.modified]="isItemModified(item)" placeholder="filename.png">
      </div>

      <!-- Alt Names -->
      <div class="card-field">
        <label class="card-label">Alternative Names</label>
        <div class="alt-names-row-mobile">
          <input #mobileAltInput type="text" class="card-input"
            placeholder="Add alternative name"
            (keydown)="addAlternativeNameFromInput(item, $event)">
          <button class="btn-add-mobile" (click)="addAlternativeName(item, mobileAltInput)">Add</button>
        </div>
        <div class="alt-tags-mini" *ngIf="item.alternativeNames?.length">
          <span *ngFor="let alt of item.alternativeNames; let i = index" class="alt-tag-mini">
            {{ alt }}
            <button (click)="removeAlternativeName(item, i)"><i class="fas fa-times"></i></button>
          </span>
        </div>
      </div>

      <!-- Prices Grid -->
      <div class="card-grid-2">
        <div class="card-field">
          <label class="card-label">Unit</label>
          <select [(ngModel)]="item.unit" class="card-input" [class.modified]="isItemModified(item)">
            <option value="Kg">Kg</option>
            <option value="Pc">Pc</option>
          </select>
        </div>
        <div class="card-field">
          <label class="card-label">Sort Order</label>
          <input type="number" [(ngModel)]="item.sortOrder" class="card-input"
            [class.modified]="isItemModified(item)" placeholder="0">
        </div>
        <div class="card-field">
          <label class="card-label">Regular Price (₹)</label>
          <input type="number" [(ngModel)]="item.regularPrice" step="0.01" class="card-input"
            [class.modified]="isItemModified(item)" placeholder="0.00">
        </div>
        <div class="card-field">
          <label class="card-label">Special Price (₹)</label>
          <input type="number" [(ngModel)]="item.specialPrice" step="0.01" class="card-input"
            [class.modified]="isItemModified(item)" placeholder="0.00">
        </div>
        <div class="card-field">
          <label class="card-label">Dealer %</label>
          <input type="number" [(ngModel)]="item.dealerPercentage" min="0" max="100" step="0.01"
            class="card-input" [class.modified]="isItemModified(item)" placeholder="0">
        </div>
        <div class="card-field card-field-full">
          <label class="card-label">Customer Price (₹)</label>
          <input type="number" class="card-input computed-price-input"
            [class.modified]="isItemModified(item)"
            [value]="getGlobalCustomerPrice(item).toFixed(2)"
            (change)="onGlobalCustomerPriceChange(item, $any($event.target).value, $any($event.target))"
            min="0" [max]="item.regularPrice" step="0.01">
        </div>
      </div>

      <!-- Toggles -->
      <div class="card-toggles">
        <label class="card-toggle">
          <input type="checkbox" [(ngModel)]="item.isActive">
          <span [class]="item.isActive ? 'pill pill-green' : 'pill pill-red'">
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </span>
        </label>
        <label class="card-toggle">
          <input type="checkbox" [(ngModel)]="item.isFeatured">
          <span [class]="item.isFeatured ? 'pill pill-blue' : 'pill pill-gray'">
            {{ item.isFeatured ? 'Featured' : 'Regular' }}
          </span>
        </label>
        <label class="card-toggle">
          <input type="checkbox" [(ngModel)]="item.isScrap">
          <span [class]="item.isScrap ? 'pill pill-purple' : 'pill pill-gray'">
            Scrap: {{ item.isScrap ? 'Yes' : 'No' }}
          </span>
        </label>
        <label class="card-toggle">
          <input type="checkbox" [(ngModel)]="item.isVisibleToCustomer">
          <span [class]="item.isVisibleToCustomer ? 'pill pill-teal' : 'pill pill-gray'">
            Visible: {{ item.isVisibleToCustomer ? 'Yes' : 'No' }}
          </span>
        </label>
      </div>

      <!-- Delete -->
      <div class="card-footer">
        <button class="btn-delete-card" (click)="requestDelete(item)">
          <i class="fas fa-trash"></i> Delete
        </button>
      </div>
    </div>
  </div>

  <!-- ── Mobile Overrides Cards ──────────────────────────────────── -->
  <div *ngIf="viewMode === 'groups' && !loading && selectedPricingGroup && getFilteredItems().length > 0" class="mobile-list">
    <div *ngFor="let item of getFilteredItems(); trackBy: trackByScrapItemId"
      class="mobile-card" [class.mobile-card-modified]="isOverrideModified(item)">

      <!-- Card Header -->
      <div class="card-header">
        <label class="card-toggle">
          <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].overridden">
          <span [class]="overrideStates[item.scrapItemId].overridden ? 'pill pill-green' : 'pill pill-gray'">
            {{ overrideStates[item.scrapItemId].overridden ? 'Overridden' : 'Use Default' }}
          </span>
        </label>
        <div class="card-avatar" *ngIf="!shouldShowFallback(item)">
          <img [src]="item.imageName" [alt]="item.scrapName"
            (error)="onImageError($event, item)" (load)="onImageLoad($event, item)">
        </div>
        <div class="card-avatar fallback" *ngIf="shouldShowFallback(item)"
          [class]="getFallbackStyle(item)">
          {{ item.scrapName.charAt(0).toUpperCase() }}
        </div>
        <div class="card-title-area">
          <div style="font-weight: 600; color: #111827;">{{ item.scrapName }}</div>
          <div class="card-meta">
            <span class="cat-badge">{{ item.scrapCategory }}</span>
            <i *ngIf="isOverrideModified(item)" class="fas fa-edit modified-indicator"></i>
          </div>
        </div>
      </div>

      <!-- Overrides Grid (only visible if overridden) -->
      <div class="card-grid-2" *ngIf="overrideStates[item.scrapItemId].overridden">
        <div class="card-field">
          <label class="card-label">Override Type</label>
          <select [(ngModel)]="overrideStates[item.scrapItemId].pricingType" class="card-input">
            <option [ngValue]="0">Percentage (%)</option>
            <option [ngValue]="1">Fixed (₹)</option>
          </select>
        </div>
        <div class="card-field">
          <label class="card-label">Override Value</label>
          <input type="number" [(ngModel)]="overrideStates[item.scrapItemId].value" class="card-input" placeholder="Value">
        </div>
        <div class="card-field card-field-full">
          <label class="card-label">Dealer Commission (%)</label>
          <input type="number" [(ngModel)]="overrideStates[item.scrapItemId].dealerPercentage" [placeholder]="item.dealerPercentage" class="card-input" min="0" max="100" step="0.1">
        </div>
        <div class="card-field">
          <label class="card-label">Preview Regular (₹)</label>
          <input type="number" class="card-input preview-input-reg"
            [value]="getPreviewPrice(item).toFixed(2)"
            (change)="onPreviewRegularPriceChange(item, $any($event.target).value, $any($event.target))"
            min="0" step="0.5">
        </div>
        <div class="card-field">
          <label class="card-label">Preview Customer (₹)</label>
          <input type="number" class="card-input preview-input-cust"
            [value]="getPreviewCustomerPrice(item).toFixed(2)"
            (change)="onPreviewCustomerPriceChange(item, $any($event.target).value, $any($event.target))"
            min="0" [max]="getPreviewPrice(item)" step="0.5">
        </div>
        <div class="card-field">
          <label class="card-label">Active State</label>
          <label class="card-toggle" style="margin-top: 0.25rem;">
            <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].isActive">
            <span [class]="overrideStates[item.scrapItemId].isActive ? 'pill pill-green' : 'pill pill-red'">
              Active: {{ overrideStates[item.scrapItemId].isActive ? 'Yes' : 'No' }}
            </span>
          </label>
        </div>
        <div class="card-field">
          <label class="card-label">Visible State</label>
          <label class="card-toggle" style="margin-top: 0.25rem;">
            <input type="checkbox" [(ngModel)]="overrideStates[item.scrapItemId].isVisibleToCustomer">
            <span [class]="overrideStates[item.scrapItemId].isVisibleToCustomer ? 'pill pill-teal' : 'pill pill-gray'">
              Visible: {{ overrideStates[item.scrapItemId].isVisibleToCustomer ? 'Yes' : 'No' }}
            </span>
          </label>
        </div>
      </div>

      <!-- Preview Prices Summary -->
      <div class="card-grid-2" style="background: #f8fafc; padding: 0.75rem; border-radius: 8px; border: 1px solid #e2e8f0;">
        <div class="card-field">
          <label class="card-label" style="font-size: 0.7rem;">Global Reg / Cust Price</label>
          <div style="font-size: 0.85rem; color: #475569; font-weight: 500;">
            ₹{{ item.regularPrice.toFixed(2) }} / ₹{{ (item.regularPrice - (item.regularPrice * item.dealerPercentage / 100)).toFixed(2) }}
          </div>
        </div>
        <div class="card-field">
          <label class="card-label" style="font-size: 0.7rem;">Resolved Reg / Cust Price</label>
          <div style="font-size: 0.85rem; font-weight: 700; color: #15803d;">
            ₹{{ getPreviewPrice(item).toFixed(2) }} / <span style="color: #1d4ed8;">₹{{ getPreviewCustomerPrice(item).toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ── Empty States ────────────────────────────────────────────── -->
  <div *ngIf="!loading && getFilteredItems().length === 0 && scrapItems.length > 0 && (viewMode === 'global' || selectedPricingGroup)" class="empty-state">
    <i class="fas fa-filter"></i>
    <h3>No items match your filters</h3>
    <p>Try clearing the search or changing the category.</p>
    <button class="btn btn-ghost" (click)="onCategoryChange('all'); searchTerm = ''; applyFilter()">
      Clear Filters
    </button>
  </div>

  <div *ngIf="viewMode === 'groups' && !loading && pricingGroups.length === 0" class="empty-state">
    <i class="fas fa-layer-group"></i>
    <h3>No pricing groups created yet</h3>
    <p>Pricing groups must be created via the backend API. Once created, they will appear here for configuration.</p>
  </div>

  <div *ngIf="viewMode === 'groups' && !loading && pricingGroups.length > 0 && !selectedPricingGroup" class="empty-state">
    <i class="fas fa-hand-pointer"></i>
    <h3>No pricing group selected</h3>
    <p>Please select a pricing group from the dropdown list to manage overrides.</p>
  </div>

  <div *ngIf="!loading && scrapItems.length === 0" class="empty-state">
    <i class="fas fa-inbox"></i>
    <h3>No scrap items yet</h3>
    <p>Create your first scrap item using the <strong>New Item</strong> button above.</p>
  </div>

  <!-- ── Footer Summary ──────────────────────────────────────────── -->
  <div *ngIf="!loading && getFilteredItems().length > 0 && (viewMode === 'global' || selectedPricingGroup)" class="summary-bar">
    <span *ngIf="viewMode === 'global'">
      <ng-container *ngIf="selectedCategory === 'all'">Total: <strong>{{ scrapItems.length }}</strong> items</ng-container>
      <ng-container *ngIf="selectedCategory !== 'all'">Showing <strong>{{ getFilteredItems().length }}</strong> of <strong>{{ scrapItems.length }}</strong> items in <em>{{ selectedCategory }}</em></ng-container>
    </span>
    <span *ngIf="viewMode === 'groups'">
      Pricing group overrides configured: <strong>{{ selectedPricingGroup?.items?.length || 0 }}</strong> item override{{ selectedPricingGroup?.items?.length !== 1 ? 's' : '' }}
    </span>

    <span *ngIf="viewMode === 'global' && hasChanges()" class="summary-modified">
      <i class="fas fa-exclamation-triangle"></i>
      {{ getModifiedCount() }} modified — click <strong>Save Changes</strong> to apply
    </span>
    <span *ngIf="viewMode === 'groups' && hasGroupChanges()" class="summary-modified">
      <i class="fas fa-exclamation-triangle"></i>
      {{ getGroupModifiedCount() }} overrides modified — click <strong>Save Changes</strong> to apply
    </span>
    <span class="summary-note" *ngIf="viewMode === 'global' && !hasChanges()">
      Only modified items are sent when saving.
    </span>
    <span class="summary-note" *ngIf="viewMode === 'groups' && !hasGroupChanges()">
      Modifications to pricing group overrides must be saved.
    </span>
  </div>

  <!-- ── Delete Confirmation ─────────────────────────────────────── -->
  <div *ngIf="showDeleteModal" class="confirm-overlay" (click)="cancelDelete()">
    <div class="confirm-dialog" (click)="$event.stopPropagation()">
      <div class="confirm-icon"><i class="fas fa-trash-alt"></i></div>
      <h3>Delete Scrap Item</h3>
      <p>Are you sure you want to delete <strong>{{ itemPendingDelete?.scrapName }}</strong>?
        This action cannot be undone.</p>
      <div class="confirm-actions">
        <button class="btn btn-ghost" (click)="cancelDelete()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDelete()">
          <i class="fas fa-trash"></i> Delete
        </button>
      </div>
    </div>
  </div>




</div>
